/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'
import moment from 'moment'

import { EuiPanel, EuiText, EuiTitle, EuiSpacer } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import useBillingDetails from '@modules/billing-lib/billingDetails/useBillingDetails'
import {
  isAnnualAndOverages,
  isDrawdown,
  isInactive,
  isMarketplaceCustomer,
  isMonthly,
  isPrepaidConsumptionCustomer,
} from '@modules/billing-lib/billingDetails/utils'

import creditCardIllustration from '@/files/credit-card-with-bubbles.svg'

import MarketplacePaymentDetails from './MarketplacePaymentDetails'
import PurchaseOrderPaymentDetails from './PurchaseOrderPaymentDetails'
import CreditCardPaymentDetails from './CreditCardPaymentDetails'
import InactiveDetails from './InactiveDetails'

import type { FunctionComponent } from 'react'

export interface Props {
  organizationId: string
}

const PaymentDetailsCard: FunctionComponent<Props> = ({ organizationId }) => {
  const nextMonth = moment().endOf(`month`).add(1, `days`)
  const nextBillDate = nextMonth.format(`MMM DD, YYYY`)
  const billingDetails = useBillingDetails()

  const getTitle = useMemo(() => {
    if (
      billingDetails.data &&
      (isMonthly(billingDetails.data) || isInactive(billingDetails.data))
    ) {
      return (
        <FormattedMessage id='payment-details-card.statement.title' defaultMessage='Next Payment' />
      )
    }

    return (
      <FormattedMessage id='payment-details-card.payments.title' defaultMessage='Next Statement' />
    )
  }, [billingDetails.data])

  const renderPaymentDetails = useMemo(() => {
    if (!billingDetails.data) {
      return null
    }

    if (isMarketplaceCustomer(billingDetails.data)) {
      return (
        <MarketplacePaymentDetails
          organizationId={organizationId}
          channel={billingDetails.data.channel!}
        />
      )
    }

    if (
      isPrepaidConsumptionCustomer(billingDetails.data) ||
      isAnnualAndOverages(billingDetails.data) ||
      isDrawdown(billingDetails.data)
    ) {
      return <PurchaseOrderPaymentDetails organizationId={organizationId} />
    }

    if (isMonthly(billingDetails.data)) {
      return <CreditCardPaymentDetails organizationId={organizationId} />
    }

    if (isInactive(billingDetails.data)) {
      return <InactiveDetails organizationId={organizationId} />
    }

    return null
  }, [billingDetails.data, organizationId])

  return (
    <EuiPanel
      paddingSize='l'
      hasShadow={false}
      hasBorder={true}
      data-test-id='payment-details-card'
    >
      <div
        style={{
          backgroundImage: `url("${creditCardIllustration}")`,
          backgroundPosition: 'top right',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <EuiText>
          <h6>{getTitle}</h6>
        </EuiText>

        <EuiSpacer size='m' />

        <EuiTitle size='m' data-test-id='nextBillDate'>
          <span>{nextBillDate}</span>
        </EuiTitle>

        <EuiSpacer size='m' />

        {renderPaymentDetails}
      </div>
    </EuiPanel>
  )
}

export default PaymentDetailsCard
